/**
 * Utility for managing tax transaction ordering in localStorage
 */

const STORAGE_KEY = 'tax_transaction_orderings'

export interface TaxOrderingData {
  [matchId: string]: {
    bol: {
      [transactionId: string]: number
    }
    invoice: {
      [transactionId: string]: number
    }
  }
}

/**
 * Get all tax transaction orderings from localStorage
 */
export function getAllOrderings(): TaxOrderingData {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : {}
  } catch (error) {
    console.error('Error reading tax transaction orderings:', error)
    return {}
  }
}

/**
 * Get orderings for a specific match
 */
export function getMatchOrderings(matchId: string): { bol: Record<string, number>; invoice: Record<string, number> } {
  const allOrderings = getAllOrderings()
  return allOrderings[matchId] || { bol: {}, invoice: {} }
}

/**
 * Save orderings for a specific match and type
 */
export function saveOrderings(matchId: string, type: 'bol' | 'invoice', orderings: Record<string, number>): void {
  try {
    const allOrderings = getAllOrderings()

    // Initialize match data if it doesn't exist
    if (!allOrderings[matchId]) {
      allOrderings[matchId] = { bol: {}, invoice: {} }
    }

    // Update the specific type orderings
    allOrderings[matchId][type] = orderings

    // Save back to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allOrderings))
  } catch (error) {
    console.error('Error saving tax transaction orderings:', error)
  }
}

/**
 * Clear orderings for a specific match
 */
export function clearMatchOrderings(matchId: string): void {
  try {
    const allOrderings = getAllOrderings()
    delete allOrderings[matchId]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allOrderings))
  } catch (error) {
    console.error('Error clearing match orderings:', error)
  }
}

/**
 * Apply saved ordering to a list of tax transactions
 */
export function applySavedOrdering<T extends { id: string }>(
  transactions: T[],
  savedOrderings: Record<string, number>
): T[] {
  // If no saved orderings, return original array
  if (!savedOrderings || Object.keys(savedOrderings).length === 0) {
    return transactions
  }

  // Create a copy and sort by saved order
  return [...transactions].sort((a, b) => {
    const orderA = savedOrderings[a.id] ?? Number.MAX_VALUE
    const orderB = savedOrderings[b.id] ?? Number.MAX_VALUE

    // If both have saved orders, sort by order
    if (orderA !== Number.MAX_VALUE && orderB !== Number.MAX_VALUE) {
      return orderA - orderB
    }

    // If only one has saved order, it comes first
    if (orderA !== Number.MAX_VALUE) return -1
    if (orderB !== Number.MAX_VALUE) return 1

    // If neither has saved order, maintain original order
    return 0
  })
}

/**
 * Generate ordering map from an ordered array
 */
export function generateOrderingMap<T extends { id: string }>(transactions: T[]): Record<string, number> {
  const orderingMap: Record<string, number> = {}

  transactions.forEach((transaction, index) => {
    orderingMap[transaction.id] = index
  })

  return orderingMap
}
